"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDegreePlans = exports.DegreePlansProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * DegreePlansProvider manages degree plan state
 *
 * Provides normalized storage of degree plans with:
 * - Fetch degree plans by organization
 * - Fetch individual degree plan by ID
 * - Update degree plans (cohort and student)
 * - Selectors for filtered views
 */
const react_1 = require("react");
const degreePlanApi_1 = require("../helpers/degreePlanApi");
const DegreePlansContext = (0, react_1.createContext)(undefined);
/**
 * DegreePlansProvider component that manages degree plan state
 */
const DegreePlansProvider = ({ children }) => {
    const [state, setState] = (0, react_1.useState)({
        items: {},
        loading: false,
        error: null,
    });
    // Ref to access latest state.items synchronously without the setState-to-read anti-pattern
    const itemsRef = (0, react_1.useRef)(state.items);
    itemsRef.current = state.items;
    /**
     * Fetch all degree plans for an organization
     */
    const fetchDegreePlansByOrg = (0, react_1.useCallback)((orgId) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const plans = yield degreePlanApi_1.degreePlanApi.getDegreePlansByOrg(orgId);
            // Normalize by ID
            const normalized = plans.reduce((acc, plan) => {
                acc[plan.id] = plan;
                return acc;
            }, {});
            setState((prev) => (Object.assign(Object.assign({}, prev), { items: normalized, loading: false })));
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to fetch degree plans' })));
            throw error;
        }
    }), []);
    /**
     * Fetch cohort plans only for an organization
     */
    const fetchCohortPlansByOrg = (0, react_1.useCallback)((orgId) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const plans = yield degreePlanApi_1.degreePlanApi.getCohortPlansByOrg(orgId);
            const normalized = plans.reduce((acc, plan) => {
                acc[plan.id] = plan;
                return acc;
            }, {});
            setState((prev) => (Object.assign(Object.assign({}, prev), { items: normalized, loading: false })));
            return plans;
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to fetch cohort plans' })));
            throw error;
        }
    }), []);
    /**
     * Fetch student plans by cohort ID
     */
    const fetchStudentPlansByCohortId = (0, react_1.useCallback)((cohortId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const plans = yield degreePlanApi_1.degreePlanApi.getStudentPlansByCohortId(cohortId);
            setState((prev) => {
                const newItems = Object.assign({}, prev.items);
                plans.forEach((plan) => {
                    newItems[plan.id] = plan;
                });
                return Object.assign(Object.assign({}, prev), { items: newItems });
            });
            return plans;
        }
        catch (error) {
            throw error;
        }
    }), []);
    /**
     * Fetch a single degree plan by ID
     */
    const fetchDegreePlanById = (0, react_1.useCallback)((id) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const plan = yield degreePlanApi_1.degreePlanApi.getDegreePlanById(id);
            setState((prev) => (Object.assign(Object.assign({}, prev), { items: Object.assign(Object.assign({}, prev.items), { [plan.id]: plan }), loading: false })));
            return plan;
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to fetch degree plan' })));
            throw error;
        }
    }), []);
    /**
     * Fetch degree plan by cohort ID
     */
    const fetchDegreePlanByCohortId = (0, react_1.useCallback)((cohortId) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const plan = yield degreePlanApi_1.degreePlanApi.getDegreePlanByCohortId(cohortId);
            setState((prev) => (Object.assign(Object.assign({}, prev), { items: Object.assign(Object.assign({}, prev.items), { [plan.id]: plan }), loading: false })));
            return plan;
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to fetch degree plan by cohort' })));
            throw error;
        }
    }), []);
    /**
     * Update a degree plan (student or cohort)
     */
    const updateDegreePlan = (0, react_1.useCallback)((id, updates) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const existingPlan = itemsRef.current[id];
            if (!existingPlan) {
                throw new Error(`Degree plan ${id} not found in state`);
            }
            const updatedPlan = existingPlan.type === 'student'
                ? yield degreePlanApi_1.degreePlanApi.updateStudentPlan(Object.assign(Object.assign({}, updates), { id }))
                : yield degreePlanApi_1.degreePlanApi.updateCohortPlan(Object.assign(Object.assign({}, updates), { id }));
            setState((prev) => (Object.assign(Object.assign({}, prev), { items: Object.assign(Object.assign({}, prev.items), { [updatedPlan.id]: updatedPlan }), loading: false })));
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to update degree plan' })));
            throw error;
        }
    }), []); // No dependencies needed
    /**
     * Delete a student degree plan
     */
    const deleteStudentPlan = (0, react_1.useCallback)((id) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            yield degreePlanApi_1.degreePlanApi.deleteStudentPlan(id);
            setState((prev) => {
                const newItems = Object.assign({}, prev.items);
                delete newItems[id];
                return Object.assign(Object.assign({}, prev), { items: newItems, loading: false });
            });
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to delete degree plan' })));
            throw error;
        }
    }), []);
    /**
     * Get all degree plans as an array
     */
    const getDegreePlansArray = (0, react_1.useCallback)(() => {
        return Object.values(state.items);
    }, [state.items]);
    /**
     * Get only cohort master plans
     */
    const getCohortMasterPlans = (0, react_1.useCallback)(() => {
        return Object.values(state.items).filter((plan) => plan.type === 'cohort');
    }, [state.items]);
    /**
     * Get only student plans
     */
    const getStudentPlans = (0, react_1.useCallback)(() => {
        return Object.values(state.items).filter((plan) => plan.type === 'student');
    }, [state.items]);
    /**
     * Get a specific degree plan by ID
     */
    const getDegreePlanById = (0, react_1.useCallback)((id) => {
        return state.items[id];
    }, [state.items]);
    const value = (0, react_1.useMemo)(() => (Object.assign(Object.assign({}, state), { fetchDegreePlansByOrg,
        fetchCohortPlansByOrg,
        fetchDegreePlanById,
        fetchDegreePlanByCohortId,
        fetchStudentPlansByCohortId,
        updateDegreePlan,
        deleteStudentPlan,
        getDegreePlansArray,
        getCohortMasterPlans,
        getStudentPlans,
        getDegreePlanById })), [
        state,
        fetchDegreePlansByOrg,
        fetchCohortPlansByOrg,
        fetchDegreePlanById,
        fetchDegreePlanByCohortId,
        fetchStudentPlansByCohortId,
        updateDegreePlan,
        deleteStudentPlan,
        getDegreePlansArray,
        getCohortMasterPlans,
        getStudentPlans,
        getDegreePlanById,
    ]);
    return (0, jsx_runtime_1.jsx)(DegreePlansContext.Provider, { value: value, children: children });
};
exports.DegreePlansProvider = DegreePlansProvider;
/**
 * Custom hook to access degree plans context
 */
const useDegreePlans = () => {
    const context = (0, react_1.useContext)(DegreePlansContext);
    if (!context) {
        throw new Error('useDegreePlans must be used within a DegreePlansProvider');
    }
    return context;
};
exports.useDegreePlans = useDegreePlans;
